/**
 * Leymen Jewels - Bazi Discovery Popup
 * Version: 1.2
 * Trigger: 10s idle | SessionStorage suppression
 * Screens: 3 (Tile Interaction -> Education -> CTA)
 *
 * v1.1 changes from spec v1.0: removed em-dashes from all customer-facing
 * copy per docs/brand-voice.md standing rule (no exceptions for popups).
 * Fixed one UK spelling (recognisable -> recognizable). Klaviyo integration
 * confirmed in-house, shipped as originally spec'd, no fallback needed.
 * Fixed popup_tile_selected firing with param key `selection` instead of
 * the spec'd `tile_selection` - would have broken cross-event GA4 analysis
 * since every other event in the table uses `tile_selection`.
 *
 * v1.2 (pre-launch copy-check): replaced "prescribes"/"prescription" (4
 * occurrences) with "reveals"/"designed to restore it" - medical/diagnostic
 * language banned per brand-voice.md Standing Rule 4, even metaphorically.
 *
 * v1.3: fixed Klaviyo marketing consent. identify()/track() never set
 * subscriptions.email.marketing.consent - only actually setting that field
 * does. Added a checked-by-default consent checkbox; checked calls a small
 * Cloudflare Worker (cloudflare-worker/worker.js) that sets consent
 * server-side, unchecked falls back to identify() alone so Blueprint
 * delivery still works without adding the profile to marketing. Targets the
 * "Email List" (Sbp2y3), switched from double to single opt-in so consent
 * applies immediately rather than after an email confirmation click.
 *
 * Note: Klaviyo's own client-side "Create Client Subscription" endpoint
 * looked correct per their docs and even passed a curl test, but real
 * browser testing found its CORS preflight is broken on Klaviyo's side
 * (never lists POST in Access-Control-Allow-Methods) - curl doesn't enforce
 * CORS, so that test was a false positive. Their legacy AJAX fallback
 * (manage.kmail-lists.com) also looked fine (returns success:true) but
 * silently doesn't subscribe anyone - only caught by checking the resulting
 * profile via the Profiles API instead of trusting the response. Hence the
 * Worker: it calls Klaviyo's server-side bulk-subscribe endpoint instead,
 * where CORS doesn't apply.
 */

(function () {
  'use strict';

  // === Config ==================================================================

  var BAZI_TEST_URL      = '/pages/bazi-test';
  var STORAGE_KEY        = 'baziPopupShown';
  var TRIGGER_MS         = 10000;
  var CONSENT_ENDPOINT   = 'https://leymen-klaviyo-consent.leymen-klaviyo-consent.workers.dev';

  // === Copy Bank ================================================================

  var TILES = [
    { id: 'scattered', icon: '⚡', text: 'Scattered and overwhelmed' },
    { id: 'stuck',     icon: '🌿', text: 'Stuck and unable to move forward' },
    { id: 'drifting',  icon: '🌊', text: 'Drifting and disconnected' }
  ];

  var SCREEN2_COPY = {
    scattered: {
      headline: 'That restlessness has an elemental root.',
      body: [
        'Bazi (八字) is a 2,000-year-old Chinese system that reads your birth date as eight elemental characters. Those characters reveal which of the Five Elements your energy is missing. Scattered energy is a known signature of elemental imbalance.',
        'A 2-minute test reads your exact chart and reveals the crystals designed to restore it.'
      ]
    },
    stuck: {
      headline: 'Stagnation is not a personal failing. It is an elemental signal.',
      body: [
        'Bazi (八字) is a 2,000-year-old Chinese system that reads your birth date as eight elemental characters. Blocked momentum is a recognizable pattern in the chart: one that points to a specific elemental deficiency.',
        'A 2-minute test reads your exact chart and reveals the crystals designed to restore it.'
      ]
    },
    drifting: {
      headline: 'Disconnection is your chart asking for grounding.',
      body: [
        'Bazi (八字) is a 2,000-year-old Chinese system that reads your birth date as eight elemental characters. A sense of drift is one of the clearest signals of elemental imbalance. It is also one of the most precisely addressable.',
        'A 2-minute test reads your exact chart and reveals the crystals designed to restore it.'
      ]
    }
  };

  // === State ====================================================================

  var currentScreen = 1;
  var selectedTile  = null;
  var overlayEl     = null;

  // === Analytics ================================================================

  function track(eventName, params) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params || {});
      }
    } catch (e) {}
  }

  // === Klaviyo Email Capture ====================================================
  // Both consented and unconsented submissions go through the same Cloudflare
  // Worker (cloudflare-worker/worker.js), which holds the private Klaviyo key
  // server-side. Two reasons this isn't done directly from here:
  // 1. Setting marketing consent needs a server-side call - Klaviyo's
  //    client-side "Create Client Subscription" endpoint looked right per
  //    their docs, but its CORS preflight is broken on Klaviyo's side (never
  //    lists POST in Access-Control-Allow-Methods), and their legacy AJAX
  //    fallback silently no-ops instead of subscribing anyone.
  // 2. Plain profile capture (no consent) used to go through window._learnq,
  //    but no klaviyo.js is loaded anywhere in this theme, so that queue had
  //    no consumer and silently did nothing - the Worker replaces that too.

  function sendToKlaviyo(email, consented, tileSelection) {
    try {
      fetch(CONSENT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email, consent: consented, tileSelection: tileSelection })
      }).catch(function () {});
    } catch (e) {}
  }

  // === HTML Builder ==============================================================

  function buildTilesHTML() {
    return TILES.map(function (tile) {
      return [
        '<button class="blj-popup__tile" data-tile="' + tile.id + '" type="button">',
        '  <span class="blj-popup__tile-icon">' + tile.icon + '</span>',
        '  <span class="blj-popup__tile-text">' + tile.text + '</span>',
        '</button>'
      ].join('');
    }).join('');
  }

  function buildPopupHTML() {
    return [
      '<div class="blj-popup-overlay" id="bljOverlay" role="dialog" aria-modal="true" aria-label="Discover your Elemental Blueprint">',
      '  <div class="blj-popup">',

      // Close
      '    <button class="blj-popup__close" id="bljClose" type="button" aria-label="Close">&#x2715;</button>',

      // Progress dots
      '    <div class="blj-popup__progress" aria-hidden="true">',
      '      <span class="blj-popup__dot blj-is-active"></span>',
      '      <span class="blj-popup__dot"></span>',
      '      <span class="blj-popup__dot"></span>',
      '    </div>',

      // Screen 1
      '    <div class="blj-popup__screen blj-is-active" id="bljScreen1">',
      '      <p class="blj-popup__eyebrow">Your Elemental Blueprint</p>',
      '      <h2 class="blj-popup__headline">Most people are running on a depleted element without knowing it.</h2>',
      '      <p class="blj-popup__sub">What feels most true for you right now?</p>',
      '      <div class="blj-popup__tiles">' + buildTilesHTML() + '</div>',
      '    </div>',

      // Screen 2
      '    <div class="blj-popup__screen" id="bljScreen2">',
      '      <p class="blj-popup__eyebrow">Your Elemental Blueprint</p>',
      '      <h2 class="blj-popup__headline" id="bljS2Headline"></h2>',
      '      <div class="blj-popup__body" id="bljS2Body"></div>',
      '      <button class="blj-popup__btn" id="bljS2Advance" type="button">Show me how &#x2192;</button>',
      '      <div class="blj-popup__nav">',
      '        <button class="blj-popup__back" id="bljS2Back" type="button">&#x2190; Back</button>',
      '      </div>',
      '    </div>',

      // Screen 3
      '    <div class="blj-popup__screen" id="bljScreen3">',
      '      <p class="blj-popup__eyebrow">Your Elemental Blueprint</p>',
      '      <h2 class="blj-popup__headline">Your Elemental Blueprint is waiting.</h2>',
      '      <p class="blj-popup__sub">Enter your birth date. Discover your imbalance, and the crystals designed to restore it. Free.</p>',
      '      <a href="' + BAZI_TEST_URL + '" class="blj-popup__btn blj-popup__btn--primary" id="bljBaziCTA">&#x2192; Take the Free Bazi Test</a>',
      '      <div class="blj-popup__secondary">',
      '        <button class="blj-popup__secondary-link" id="bljEmailToggle" type="button">',
      '          Not ready? Enter your email and we\'ll send your Blueprint when you are.',
      '        </button>',
      '        <div class="blj-popup__email-form" id="bljEmailForm">',
      '          <input type="email" class="blj-popup__email-input" id="bljEmailInput" placeholder="Your email address" autocomplete="email" />',
      '          <label class="blj-popup__consent" for="bljConsentCheckbox">',
      '            <input type="checkbox" id="bljConsentCheckbox" checked />',
      '            <span>I\'d like to receive chart insights and Bazi guidance by email.</span>',
      '          </label>',
      '          <button class="blj-popup__email-submit" id="bljEmailSubmit" type="button">Send My Blueprint &#x2192;</button>',
      '        </div>',
      '        <div class="blj-popup__confirmation" id="bljConfirmation">',
      '          <p>Your Blueprint is on its way.<br>Check your inbox shortly.</p>',
      '        </div>',
      '      </div>',
      '      <div class="blj-popup__nav">',
      '        <button class="blj-popup__back" id="bljS3Back" type="button">&#x2190; Back</button>',
      '      </div>',
      '      <button class="blj-popup__dismiss" id="bljDismiss" type="button">I\'ll explore on my own</button>',
      '    </div>',

      '  </div>',
      '</div>'
    ].join('\n');
  }

  // === Screen Navigation =========================================================

  function updateDots(screenNum) {
    var dots = document.querySelectorAll('.blj-popup__dot');
    for (var i = 0; i < dots.length; i++) {
      dots[i].classList.toggle('blj-is-active', i < screenNum);
    }
  }

  function showScreen(screenNum) {
    var screens = document.querySelectorAll('.blj-popup__screen');
    for (var i = 0; i < screens.length; i++) {
      screens[i].classList.remove('blj-is-active');
    }
    document.getElementById('bljScreen' + screenNum).classList.add('blj-is-active');
    updateDots(screenNum);
    currentScreen = screenNum;
    track('popup_screen_' + screenNum + '_viewed', { tile_selection: selectedTile || 'none' });
  }

  function populateScreen2(tileId) {
    var copy = SCREEN2_COPY[tileId];
    document.getElementById('bljS2Headline').textContent = copy.headline;
    var bodyEl = document.getElementById('bljS2Body');
    bodyEl.innerHTML = copy.body.map(function (p) {
      return '<p>' + p + '</p>';
    }).join('');
  }

  // === Close =====================================================================

  function closePopup() {
    if (!overlayEl) return;
    track('popup_dismissed', { screen: currentScreen, tile_selection: selectedTile || 'none' });
    overlayEl.classList.remove('blj-is-visible');
    setTimeout(function () {
      if (overlayEl && overlayEl.parentNode) {
        overlayEl.parentNode.removeChild(overlayEl);
        overlayEl = null;
      }
    }, 400);
  }

  // === Event Binding ==============================================================

  function bindEvents() {

    // Close button
    document.getElementById('bljClose').addEventListener('click', closePopup);

    // Overlay backdrop click
    overlayEl.addEventListener('click', function (e) {
      if (e.target === overlayEl) closePopup();
    });

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlayEl) closePopup();
    });

    // Tiles - Screen 1
    var tiles = document.querySelectorAll('.blj-popup__tile');
    for (var i = 0; i < tiles.length; i++) {
      tiles[i].addEventListener('click', function () {
        selectedTile = this.getAttribute('data-tile');

        // Visual selection
        var allTiles = document.querySelectorAll('.blj-popup__tile');
        for (var j = 0; j < allTiles.length; j++) {
          allTiles[j].classList.remove('blj-is-selected');
        }
        this.classList.add('blj-is-selected');

        track('popup_tile_selected', { tile_selection: selectedTile });

        // Advance after brief delay
        var self = this;
        setTimeout(function () {
          populateScreen2(selectedTile);
          showScreen(2);
        }, 220);
      });
    }

    // Screen 2 - Advance
    document.getElementById('bljS2Advance').addEventListener('click', function () {
      showScreen(3);
    });

    // Screen 2 - Back
    document.getElementById('bljS2Back').addEventListener('click', function () {
      // Clear tile selection visually
      var allTiles = document.querySelectorAll('.blj-popup__tile');
      for (var i = 0; i < allTiles.length; i++) {
        allTiles[i].classList.remove('blj-is-selected');
      }
      selectedTile = null;
      showScreen(1);
    });

    // Screen 3 - Bazi CTA
    document.getElementById('bljBaziCTA').addEventListener('click', function () {
      track('popup_bazi_cta_clicked', { tile_selection: selectedTile || 'none' });
    });

    // Screen 3 - Back
    document.getElementById('bljS3Back').addEventListener('click', function () {
      showScreen(2);
    });

    // Screen 3 - Email toggle
    document.getElementById('bljEmailToggle').addEventListener('click', function () {
      var form = document.getElementById('bljEmailForm');
      var isVisible = form.classList.contains('blj-is-visible');
      form.classList.toggle('blj-is-visible', !isVisible);
      if (!isVisible) {
        setTimeout(function () {
          var input = document.getElementById('bljEmailInput');
          if (input) input.focus();
        }, 100);
      }
    });

    // Screen 3 - Email submit
    document.getElementById('bljEmailSubmit').addEventListener('click', function () {
      var input = document.getElementById('bljEmailInput');
      var email = input ? input.value.trim() : '';
      var consentBox = document.getElementById('bljConsentCheckbox');
      var consented = consentBox ? consentBox.checked : false;

      // Basic validation
      if (!email || email.indexOf('@') === -1 || email.indexOf('.') === -1) {
        if (input) {
          input.style.borderColor = '#C0392B';
          input.focus();
        }
        return;
      }

      // Reset border
      if (input) input.style.borderColor = '';

      // Submit to Klaviyo: consented sets marketing consent, unconsented just
      // captures the email so Blueprint delivery still works.
      sendToKlaviyo(email, consented, selectedTile || 'none');
      track('popup_email_submitted', { tile_selection: selectedTile || 'none', marketing_consent: consented });

      // Show confirmation
      document.getElementById('bljEmailForm').classList.remove('blj-is-visible');
      document.getElementById('bljEmailToggle').style.display = 'none';
      document.getElementById('bljConfirmation').classList.add('blj-is-visible');
    });

    // Dismiss
    document.getElementById('bljDismiss').addEventListener('click', closePopup);
  }

  // === Show Popup =================================================================

  function showPopup() {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    sessionStorage.setItem(STORAGE_KEY, 'true');

    // Inject into DOM
    var wrapper = document.createElement('div');
    wrapper.innerHTML = buildPopupHTML();
    var popupNode = wrapper.firstElementChild;
    document.body.appendChild(popupNode);

    overlayEl = document.getElementById('bljOverlay');

    // Trigger animation on next available frames
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        overlayEl.classList.add('blj-is-visible');
      });
    });

    bindEvents();
    track('popup_screen_1_viewed', {});
  }

  // === Init =========================================================================

  function init() {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    setTimeout(showPopup, TRIGGER_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
